import React, { useState } from 'react';
import DefaultMessageInput from '@sendbird/uikit-react/ui/MessageInput';
import { useGroupChannelContext } from '@sendbird/uikit-react/GroupChannel/context';
import styled from '@emotion/styled';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { lightGreyAlt, disabledLightGrey } from '@/shared/styles/colors';
import { LookbookOffer } from './LookbookOffer';
import { SourcedByForm } from './SourcedByForm';
import { TabBar, Tab, TabText } from '../styledComponents';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { MessageType, messageTabOptions } from './MessageInput.types';

type MessageInputProps = React.ComponentProps<typeof DefaultMessageInput>;

interface ExtendedSendMessageParams {
  message: string;
  mentionTemplate: string;
  customType?: string;
  data?: string;
}

const WrappedMessageInput: React.FC<Partial<MessageInputProps>> = (props) => {
  const { currentChannel, sendUserMessage, sendFileMessage } =
    useGroupChannelContext();
  const { isDesktop } = useBreakpoint();
  const { user } = useGetUser();

  const [messageType, setMessageType] = useState<MessageType>('default');
  const [price, setPrice] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const currentUserId = String(user?.id);
  const otherMemberId = currentChannel?.members?.find(
    (m) => m.userId !== currentUserId,
  )?.userId;
  const clientUserId = otherMemberId ? Number(otherMemberId) : undefined;

  const { ...rest } = props;

  if (!currentChannel) return null;

  const handleSendMessage = (params: {
    message: string;
    mentionTemplate: string;
  }) => {
    if (!sendUserMessage) return;

    if (messageType === 'product') {
      const messageText =
        params.message.trim() !== ''
          ? params.message
          : `Product priced at $${price}`;
      const customData = JSON.stringify({
        price,
        stylistId: user?.stylist?.id,
        ...(note.trim() && { note: note.trim() }),
      });
      const extendedParams: ExtendedSendMessageParams = {
        ...params,
        message: messageText,
        customType: 'product',
        data: customData,
      };
      sendUserMessage(extendedParams);
    } else {
      sendUserMessage(params);
      currentChannel.endTyping();
    }
  };

  const handleSendFileMessage = (files: File[]) => {
    if (!sendFileMessage) return;
    files.forEach((file) => {
      const fileMessageParams = {
        file,
        fileName: file.name,
        mimeType: file.type,
      };
      sendFileMessage(fileMessageParams);
    });
  };

  const handleProductSend = () => {
    handleSendMessage({ message: '', mentionTemplate: '' });
    setPrice('');
    setNote('');
  };

  return (
    <MessageInputRoot className="sendbird-message-input-wrapper">
      <TabBar isDesktop={isDesktop}>
        {messageTabOptions.map((tab) => (
          <Tab
            key={tab.value}
            isActive={messageType === tab.value}
            onPress={() => setMessageType(tab.value)}
          >
            <TabText isActive={messageType === tab.value}>{tab.label}</TabText>
          </Tab>
        ))}
      </TabBar>
      {messageType === 'product' && (
        <TabContentWrapper isDesktop={isDesktop}>
          <LookbookOffer
            price={price}
            onPriceChange={setPrice}
            note={note}
            onNoteChange={setNote}
            onSend={handleProductSend}
            isDesktop={isDesktop}
            disabled={!price}
          />
        </TabContentWrapper>
      )}
      {messageType === 'sourcedBy' && (
        <TabContentWrapper isDesktop={isDesktop}>
          <SourcedByForm
            onSubmitSuccess={() => setMessageType('default')}
            isDesktop={isDesktop}
            clientUserId={clientUserId}
          />
        </TabContentWrapper>
      )}
      {messageType !== 'default' ? null : (
        <InputWrapper
          isDesktop={isDesktop}
          className="sendbird-message-input-wrapper__message-input"
        >
          <DefaultMessageInput
            {...(rest as MessageInputProps)}
            onSendMessage={handleSendMessage}
            onFileUpload={handleSendFileMessage}
            onStartTyping={() => {
              currentChannel.startTyping();
            }}
            onCancelEdit={() => {
              currentChannel.endTyping();
            }}
          />
        </InputWrapper>
      )}
    </MessageInputRoot>
  );
};

export default WrappedMessageInput;

const MessageInputRoot = styled.div({
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

const TabContentWrapper = styled.div<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    ...(isDesktop
      ? { display: 'flex', justifyContent: 'center' }
      : { padding: '0 16px' }),
  }),
);

const InputWrapper = styled.div<{ isDesktop: boolean }>(({ isDesktop }) => ({
  height: isDesktop ? 76 : 136,
  padding: isDesktop ? '0 12px !important' : '0 16px !important',

  '& .sendbird-message-input button': {
    top: 0,
    bottom: 'auto',
  },

  '& .sendbird-message-input .sendbird-message-input--textarea': {
    minHeight: isDesktop ? 76 : 136,
    background: lightGreyAlt,
    border: `1px solid ${disabledLightGrey}`,
    borderRadius: 8,
  },
}));
